import { createContext, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { getTextLocale, setTextLocale } from "lib/storefront";
import { normalizeStorefrontLocale } from "lib/storefrontCopy";

const ES = {
  Account: "Cuenta",
  "Account and tools": "Cuenta y herramientas",
  Add: "Agregar",
  "Add a subject and message.": "Agregue un asunto y un mensaje.",
  "Add photos": "Agregar fotos",
  "Additional notes": "Notas adicionales",
  Address: "Dirección",
  Addresses: "Direcciones",
  "Addresses saved.": "Direcciones guardadas.",
  All: "Todo",
  Answered: "Respondido",
  "Ask a question, share photos, and get help from the VBS team.":
    "Haga una pregunta, comparta fotos y reciba ayuda del equipo VBS.",
  "All core returns": "Todas las devoluciones",
  "Approve requests or send them to VBS": "Apruebe solicitudes o envíelas a VBS",
  "Approve requests or send them to VBS.": "Apruebe solicitudes o envíelas a VBS.",
  "Assigned to your account.": "Asignados a su cuenta.",
  "Awaiting label": "Esperando etiqueta",
  "Awaiting sale": "Esperando venta",
  Available: "Disponible",
  Back: "Atrás",
  "Browse products and place orders.": "Explore productos y haga pedidos.",
  "Browse products and pricing": "Explore productos y precios",
  Carrier: "Paquetería",
  Cart: "Carrito",
  Catalog: "Catálogo",
  "Choose access.": "Elija el acceso.",
  "Choose drop-off option": "Elija dónde depositar",
  "Choose the exact place from suggestions.": "Elija el lugar exacto de las sugerencias.",
  Claim: "Reclamación",
  "Claim chat": "Chat de reclamación",
  Clear: "Libre",
  Close: "Cerrar",
  Codes: "Códigos",
  Company: "Compañía",
  Compose: "Redactar",
  "Confirm the packaging checklist before continuing.":
    "Confirme la lista de empaque antes de continuar.",
  "Confirm this core": "Confirme este core",
  Contact: "Contacto",
  "Contact details and account information": "Contacto e información de cuenta",
  "Contact details and account information.": "Contacto e información de cuenta.",
  "Contact details.": "Datos de contacto.",
  Continue: "Continuar",
  "Continue to the staff app": "Continuar a la app de staff",
  Core: "Core",
  "Core clear": "Core libre",
  "Core condition": "Condición del core",
  "Core deadline": "Fecha límite del core",
  "Core due": "Core pendiente",
  "Core program": "Programa de core",
  "Core return": "Devolución de core",
  "Core returns": "Devolución de cores",
  "Core status": "Estado del core",
  "Cores you owe": "Cores que adeuda",
  "Active returns": "Devoluciones activas",
  "Return progress": "Progreso de la devolución",
  "Return started": "Devolución iniciada",
  "Label ready": "Etiqueta lista",
  "In progress": "En progreso",
  "In transit": "En tránsito",
  "Received / inspection": "Recibido / inspección",
  Approved: "Aprobado",
  Forfeited: "Perdido",
  "1 unread message": "1 mensaje sin leer",
  "{count} unread messages": "{count} mensajes sin leer",
  "Staff ERP tracks this return with the same status.":
    "El ERP del personal da seguimiento a esta devolución con el mismo estado.",
  "Create RMA": "Crear RMA",
  "Create a warranty claim.": "Crear una reclamación de garantía.",
  "Create account": "Crear cuenta",
  "Create an account": "Crear una cuenta",
  "Create an account instead": "Crear una cuenta en su lugar",
  Customer: "Cliente",
  "Customer Portal": "Portal de cliente",
  "Customer request": "Solicitud del cliente",
  "Customer requests for your account.": "Solicitudes de clientes de su cuenta.",
  "Customers assigned to your account": "Clientes asignados a su cuenta",
  "Customers assigned to your account.": "Clientes asignados a su cuenta.",
  Deadline: "Fecha límite",
  Decision: "Decisión",
  "Deposit program": "Programa de depósito",
  "Details and tracking.": "Detalles y rastreo.",
  Distributor: "Distribuidor",
  "Distributor decision": "Decisión del distribuidor",
  Due: "Pendiente",
  Email: "Correo",
  "Enter your email and password.": "Ingrese su correo y contraseña.",
  "Fulfill from distributor": "Cumplir desde distribuidor",
  Fulfilled: "Cumplida",
  "Generating label...": "Generando etiqueta...",
  Home: "Inicio",
  "Inbound tracking": "Rastreo de entrada",
  "Install registered": "Instalación registrada",
  "Install scan": "Escaneo de instalación",
  Installer: "Instalador",
  Invoice: "Factura",
  "Invalid credentials.": "Credenciales inválidas.",
  Issue: "Problema",
  "Issue codes": "Códigos del problema",
  Jobs: "Trabajos",
  Label: "Etiqueta",
  "Label only": "Solo etiqueta",
  "Line items": "Partidas",
  Linked: "Vinculado",
  "Linked customers": "Clientes vinculados",
  "Loading...": "Cargando...",
  "Local drop-off locations": "Sucursales cercanas para depositar",
  "Nearby drop-off locations": "Sucursales cercanas para depositar",
  "{carrier} drop-off locations": "Sucursales {carrier} cercanas para depositar",
  "Log out": "Cerrar sesión",
  Login: "Iniciar sesión",
  "Login failed.": "Error al iniciar sesión.",
  Logout: "Salir",
  Make: "Marca",
  "Manage saved shipping addresses.": "Administre direcciones de envío guardadas.",
  Membership: "Membresía",
  Menu: "Menú",
  Message: "Mensaje",
  "Message support and attach photos.": "Escriba a soporte y adjunte fotos.",
  Mileage: "Kilometraje",
  Model: "Modelo",
  Name: "Nombre",
  "No distributor order requests yet.": "Aún no hay solicitudes de pedido.",
  "No linked customers found.": "No se encontraron clientes vinculados.",
  "No nearby drop-off locations were returned for this carrier. Use the carrier website locator after printing your label.":
    "No se encontraron sucursales cercanas para depositar con esta paquetería. Use el localizador del sitio después de imprimir su etiqueta.",
  "No orders yet.": "Aún no hay pedidos.",
  "No prepaid drop-off options are available right now.":
    "No hay opciones prepaga para depositar el paquete ahora.",
  "No saved addresses yet.": "Aún no hay direcciones guardadas.",
  "No valve bodies found yet.": "Aún no hay cuerpos de válvulas.",
  "No valve bodies match those filters.": "Ningún cuerpo de válvula coincide con esos filtros.",
  "No warranty claims yet.": "Aún no hay reclamaciones de garantía.",
  None: "Ninguno",
  "Not registered": "No registrado",
  "Not selected": "No seleccionado",
  Notes: "Notas",
  "Open Staff ERP": "Abrir Staff ERP",
  "Open an RMA.": "Abra un RMA.",
  "Open map": "Abrir mapa",
  "Open return": "Abrir devolución",
  Order: "Pedido",
  "Order not found.": "Pedido no encontrado.",
  "Order request": "Solicitud de pedido",
  "Order request not found.": "Solicitud de pedido no encontrada.",
  "Order requests": "Solicitudes de pedido",
  Orders: "Pedidos",
  "Orders & service": "Pedidos y servicio",
  PDF: "PDF",
  "Pack the bagged core in the original or old box so it ships securely.":
    "Empaque el core embolsado en la caja original o anterior para un envío seguro.",
  "Pack the core": "Empaque el core",
  "Packaging checklist": "Lista de empaque",
  "Packaging confirmed. Choose where to drop off your core.":
    "Empaque confirmado. Elija dónde entregar su core.",
  Part: "Parte",
  Password: "Contraseña",
  Payment: "Pago",
  Pending: "Pendiente",
  Phone: "Teléfono",
  "Pickup + label": "Recolección + etiqueta",
  "Pickup + prepaid label": "Recolección + etiqueta prepaga",
  Pipeline: "Pipeline",
  "Place the core in the provided plastic bag and seal it before boxing.":
    "Coloque el core en la bolsa de plástico incluida y séllela antes de empacarlo.",
  "Portal home and tools": "Inicio del portal y herramientas",
  "Prepaid options — no charge to you.": "Opciones prepagadas — sin costo para usted.",
  "Price tier": "Nivel de precio",
  Primary: "Principal",
  "Print label": "Imprimir etiqueta",
  "Print label and drop off": "Imprimir etiqueta y depositar",
  "Problem description": "Descripción del problema",
  Product: "Producto",
  "Product catalog": "Catálogo de productos",
  Profile: "Perfil",
  "Profile saved.": "Perfil guardado.",
  Provider: "Proveedor",
  "QR / tag": "QR / etiqueta",
  Qty: "Cant.",
  Recipient: "Destinatario",
  "Recipient name": "Nombre del destinatario",
  Redirected: "Enviada a VBS",
  "Refresh options": "Actualizar opciones",
  "Register as a customer": "Regístrese como cliente",
  Registered: "Registrado",
  "Registered owner": "Propietario registrado",
  "Registration status": "Estado de registro",
  Remove: "Eliminar",
  "Replacement order": "Pedido de reemplazo",
  Request: "Solicitud",
  "Request marked fulfilled.": "Solicitud marcada como cumplida.",
  "Request sent to VBS.": "Solicitud enviada a VBS.",
  Requested: "Solicitado",
  "Return core": "Devolver core",
  "Return owed cores in three steps.": "Devuelva cores adeudados en tres pasos.",
  "Return owed cores with a prepaid label.": "Devuelva cores adeudados con una etiqueta prepaga.",
  "Return tracking": "Rastreo de devolución",
  "Review core status.": "Revise el estado del core.",
  "Review selected catalog items.": "Revise los artículos seleccionados.",
  "Review selected items": "Revise los artículos seleccionados",
  Review: "Revisión",
  Role: "Rol",
  "Sale and install": "Venta e instalación",
  "Sale scanned": "Venta escaneada",
  Save: "Guardar",
  "Save profile": "Guardar perfil",
  "Saved for checkout.": "Guardadas para el checkout.",
  "Saved shipping addresses": "Direcciones de envío guardadas",
  "Saved shipping addresses for checkout.": "Direcciones de envío guardadas para el checkout.",
  "Saving...": "Guardando...",
  Scan: "Escanear",
  "Scan source": "Origen del escaneo",
  Search: "Buscar",
  "Search address": "Buscar dirección",
  "Search gallery images": "Buscar imágenes de la galería",
  "Search valve bodies": "Buscar cuerpos de válvulas",
  "Seal in a plastic bag": "Séllelo en una bolsa de plástico",
  "Select a verified address from suggestions for every saved address.":
    "Seleccione una dirección verificada de las sugerencias para cada dirección guardada.",
  Seller: "Vendedor",
  "Seller scan": "Escaneo del vendedor",
  "Send an update": "Enviar una actualización",
  "Send to VBS warehouse": "Enviar a almacén VBS",
  "Sending...": "Enviando...",
  Serial: "Serie",
  "Serial pending": "Serie pendiente",
  "Set primary": "Marcar principal",
  "Ship window": "Ventana de envío",
  Shipping: "Envío",
  "Shipping amount": "Monto de envío",
  "Shipping and replacement": "Envío y reemplazo",
  "Shipping option": "Opción de envío",
  Shop: "Tienda",
  "Shop and account access": "Tienda y acceso a cuenta",
  Show: "Mostrar",
  "Sign Out": "Cerrar sesión",
  "Sign in": "Iniciar sesión",
  "Sign in.": "Iniciar sesión.",
  "Sign out": "Cerrar sesión",
  "Signing in...": "Iniciando sesión...",
  "Social sign in failed.": "Error al iniciar sesión con proveedor social.",
  Sold: "Vendido",
  "Sold as": "Vendido como",
  "Sold on": "Vendido el",
  "Sold to": "Vendido a",
  "Sold units and registration.": "Unidades vendidas y registro.",
  "Sold valve bodies": "Unidades vendidas",
  Staff: "Staff",
  "Staff tools": "Herramientas de staff",
  "Start the refundable core return.": "Inicie la devolución reembolsable del core.",
  Status: "Estado",
  "Status, payment, and tracking.": "Estado, pago y rastreo.",
  "Status, shipping, and updates.": "Estado, envío y actualizaciones.",
  "Status, totals, invoices, and tracking": "Estado, totales, facturas y rastreo",
  "Status, totals, invoices, and tracking.": "Estado, totales, facturas y rastreo.",
  Stock: "Inventario",
  "Storefront home": "Inicio de tienda",
  Submitted: "Enviada",
  "Submitting...": "Enviando...",
  Subtotal: "Subtotal",
  Summary: "Resumen",
  "Support messages.": "Mensajes de soporte.",
  "The box is not leaking or dripping oil.": "La caja no está goteando ni filtrando aceite.",
  "Toggle password visibility": "Mostrar u ocultar contraseña",
  Total: "Total",
  Totals: "Totales",
  "Track return": "Rastrear devolución",
  "Track sold and registered units.": "Rastree unidades vendidas y registradas.",
  "Track the active core return.": "Rastree la devolución de core activa.",
  "Track sold and registered units": "Rastree unidades vendidas y registradas",
  Tracking: "Rastreo",
  Transmission: "Transmisión",
  Trim: "Versión",
  "Unable to create warranty claim.": "No se pudo crear la reclamación de garantía.",
  "Unable to generate the return label.": "No se pudo generar la etiqueta de devolución.",
  "Unable to load prepaid shipping options.":
    "No se pudieron cargar las opciones de envío prepago.",
  "Unable to load.": "No se pudo cargar.",
  "Unable to save addresses.": "No se pudieron guardar las direcciones.",
  "Unable to save profile.": "No se pudo guardar el perfil.",
  "Unable to start core return.": "No se pudo iniciar la devolución del core.",
  "Unable to update request.": "No se pudo actualizar la solicitud.",
  Unit: "Unidad",
  "Unit lifecycle": "Ciclo de vida de la unidad",
  Update: "Actualización",
  "Use the original box": "Use la caja original",
  "VBS order": "Pedido VBS",
  VIN: "VIN",
  "Valve Body": "Cuerpo de válvula",
  "Valve bodies": "Cuerpos de válvulas",
  "Valve body not found.": "Cuerpo de válvula no encontrado.",
  Vehicle: "Vehículo",
  "Vehicle make is required.": "La marca del vehículo es obligatoria.",
  "Vehicle model is required.": "El modelo del vehículo es obligatorio.",
  "Vehicle year": "Año del vehículo",
  "Vehicle year is required.": "El año del vehículo es obligatorio.",
  "View catalog": "Ver catálogo",
  Warranty: "Garantía",
  "Warranty claim": "Reclamación de garantía",
  "Warranty claim created.": "Reclamación de garantía creada.",
  "Warranty claim not found.": "Reclamación de garantía no encontrada.",
  "Warranty history": "Historial de garantía",
  "Warranty, core returns, and registrations": "Garantía, devolución de cores y registros",
  "Warranty, core returns, and registrations.": "Garantía, devolución de cores y registros.",
  "Warranty, cores, and lifecycle.": "Garantía, cores y ciclo de vida.",
  XML: "XML",
  "You are returning this valve body core with a prepaid VBS label.":
    "Está devolviendo este core con una etiqueta prepaga de VBS.",
  "You do not have any cores owed or active returns.":
    "No tiene cores adeudados ni devoluciones activas.",
  "Your label is ready": "Su etiqueta está lista",
  "Your prepaid return label is ready.": "Su etiqueta de devolución prepaga está lista.",
  "1. Confirm": "1. Confirmar",
  "2. Pack": "2. Empacar",
  "3. Label": "3. Etiqueta",
  "Active claim": "Reclamación activa",
  "Active RMA": "RMA activo",
  "Confirm, pack, then print your prepaid label.":
    "Confirme, empaque y luego imprima su etiqueta prepaga.",
  "Core is packed in the original box.": "El core está empacado en la caja original.",
  "Core is sealed in a plastic bag.": "El core está sellado en una bolsa de plástico.",
  "day estimate": "días estimados",
  "Drain all oil": "Drene todo el aceite",
  "Drain the core completely. Wipe residue so the box is not dripping or leaking oil.":
    "Drene el core por completo. Limpie residuos para que la caja no gotee ni filtre aceite.",
  "image selected": "imagen seleccionada",
  "images selected": "imágenes seleccionadas",
  "Problem description is required.": "La descripción del problema es obligatoria.",
  "Refundable core credit": "Crédito reembolsable del core",
  Returned: "Devuelto",
  Tag: "Etiqueta",
  chat: "chat",
  address: "dirección",
  addresses: "direcciones",
  "Access your portal": "Acceda a su portal",
  "Address pending": "Dirección pendiente",
  "Attach photos": "Adjuntar fotos",
  "Chat thread is being prepared.": "El chat se está preparando.",
  "Chat thread is being prepared for this claim.":
    "El chat se está preparando para esta reclamación.",
  Camera: "Cámara",
  Cancel: "Cancelar",
  "Chat with support": "Chatee con soporte",
  "Chat with VBS — messages and photos.": "Chat con VBS — mensajes y fotos.",
  Conversations: "Conversaciones",
  "Generate label": "Generar etiqueta",
  iMessage: "iMessage",
  Message: "Mensaje",
  Messages: "Mensajes",
  "New Message": "Nuevo mensaje",
  "New message": "Nuevo mensaje",
  "No Messages": "Sin mensajes",
  "No messages yet": "Aún no hay mensajes",
  Open: "Abierto",
  "Open label": "Abrir etiqueta",
  "Optional comment": "Comentario opcional",
  Photo: "Foto",
  Photos: "Fotos",
  photo: "foto",
  photos: "fotos",
  "Rate this conversation": "Califique esta conversación",
  "Request shipping": "Solicitar envío",
  Requested: "Solicitado",
  Resolved: "Resuelto",
  Send: "Enviar",
  "Send a message to get help.": "Envíe un mensaje para recibir ayuda.",
  "Send a text or photo — just like Messages.": "Envíe un texto o foto — como en Mensajes.",
  "Send update": "Enviar actualización",
  "Shipping label updated.": "Etiqueta de envío actualizada.",
  "Shipping option requested.": "Opción de envío solicitada.",
  "Start conversation": "Iniciar conversación",
  Subject: "Asunto",
  "Submit review": "Enviar reseña",
  Support: "Soporte",
  "Tap the compose button to message VBS Support.":
    "Toque el botón de redactar para escribir a Soporte VBS.",
  "Thanks for your feedback": "Gracias por su opinión",
  "This conversation is closed.": "Esta conversación está cerrada.",
  "To:": "Para:",
  "Track shipment": "Rastrear envío",
  "Unable to load conversations.": "No se pudieron cargar las conversaciones.",
  "Unable to send message.": "No se pudo enviar el mensaje.",
  "Unable to send update.": "No se pudo enviar la actualización.",
  "Unable to start conversation.": "No se pudo iniciar la conversación.",
  "Unable to update shipping.": "No se pudo actualizar el envío.",
  "Update received.": "Actualización recibida.",
  "Update sent.": "Actualización enviada.",
  Updates: "Actualizaciones",
  Uploaded: "Se subieron",
  "VBS Support": "Soporte VBS",
  You: "Usted",
  image: "imagen",
  images: "imágenes",
};

const MobileLocaleContext = createContext({
  locale: "en-US",
  language: "en",
  t: (value) => value,
});

function resolveMobileLocale(tenantKey, defaultLocale) {
  const key = String(tenantKey || "")
    .trim()
    .toLowerCase();
  if (key === "mx") return "es-MX";
  if (key) return normalizeStorefrontLocale(defaultLocale) || "en-US";
  return getTextLocale() || "en-US";
}

export function MobileLocaleProvider({ children }) {
  const { tenant, tenantKey } = useStorefrontTenant();
  const locale = useMemo(
    () => resolveMobileLocale(tenantKey, tenant?.defaultLocale),
    [tenant?.defaultLocale, tenantKey]
  );
  const isSpanish = String(locale).toLowerCase().startsWith("es");

  useEffect(() => {
    if (!tenantKey) return;
    setTextLocale(locale);
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.lang = isSpanish ? "es-MX" : "en-US";
    }
  }, [isSpanish, locale, tenantKey]);

  const value = useMemo(
    () => ({
      locale,
      language: isSpanish ? "es" : "en",
      isSpanish,
      t: (text) => {
        const key = String(text ?? "");
        if (!key) return key;
        return isSpanish ? ES[key] || key : key;
      },
    }),
    [isSpanish, locale]
  );

  return <MobileLocaleContext.Provider value={value}>{children}</MobileLocaleContext.Provider>;
}

MobileLocaleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useMobileLocale() {
  return useContext(MobileLocaleContext);
}
